import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    DatePicker,
    Table,
    Button,
    message,
    Checkbox,
    Space,
    Tag,
    Typography,
    Alert,
    Progress,
    Divider,
    Card,
    Col,
    Row,
    Statistic,
    Select,
    Radio
} from 'antd';
import {
    CalendarOutlined,
    UserOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { attendanceAPI } from '../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { Option } = Select;

const BulkAnnualLeaveModal = ({ visible, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [leaveType, setLeaveType] = useState('full_day');

    useEffect(() => {
        if (visible) {
            loadUsers();
        } else {
            // 모달이 닫힐 때 초기화
            form.resetFields();
            setUsers([]);
            setSelectedUserIds([]);
            setDateRange(null);
            setStatistics(null);
            setLeaveType('full_day');
        }
    }, [visible, form]);

    // 사용자 목록 로드
    const loadUsers = async () => {
        setLoading(true);
        try {
            const year = new Date().getFullYear();
            const response = await attendanceAPI.getBulkLeaveUsers({ year });
            
            if (response.data.success) {
                const usersData = response.data.users;
                setUsers(usersData);
                
                // 기본적으로 모든 사용자 선택
                const allUserIds = usersData.map(user => user.id);
                setSelectedUserIds(allUserIds);
                
                // 통계 계산
                calculateStatistics(usersData, allUserIds);
            }
        } catch (error) {
            message.error('사용자 목록 조회에 실패했습니다');
            console.error('Load users error:', error);
        } finally {
            setLoading(false);
        }
    };

    // 통계 계산
    const calculateStatistics = (allUsers, selectedIds) => {
        const selectedUsers = allUsers.filter(user => selectedIds.includes(user.id));
        
        const totalSelected = selectedUsers.length;
        const totalUsers = allUsers.length;
        const totalRemainingLeave = selectedUsers.reduce((sum, user) => sum + user.balance.remaining_annual_leave, 0);
        const lowLeaveUsers = selectedUsers.filter(user => user.balance.remaining_annual_leave < 3).length;
        
        setStatistics({
            totalUsers,
            totalSelected,
            totalRemainingLeave,
            lowLeaveUsers,
            averageRemaining: totalSelected > 0 ? totalRemainingLeave / totalSelected : 0
        });
    };

    // 날짜 범위 변경 처리
    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        
        if (dates && dates.length === 2) {
            // 선택된 기간에 이미 연차가 있는 사용자 필터링
            checkConflictingLeave(dates);
        }
    };

    // 기간 내 연차 충돌 체크 (실제 구현에서는 API 호출 필요)
    const checkConflictingLeave = (dates) => {
        // 여기서는 간단히 처리, 실제로는 각 사용자별로 해당 기간 연차 존재 여부 확인 필요
        console.log('Checking conflicting leave for period:', dates);
    };

    // 사용자 선택/해제 처리
    const handleUserSelection = (userId, checked) => {
        let newSelectedIds;
        
        if (checked) {
            newSelectedIds = [...selectedUserIds, userId];
        } else {
            newSelectedIds = selectedUserIds.filter(id => id !== userId);
        }
        
        setSelectedUserIds(newSelectedIds);
        calculateStatistics(users, newSelectedIds);
    };

    // 전체 선택/해제
    const handleSelectAll = (checked) => {
        if (checked) {
            const allIds = users.map(user => user.id);
            setSelectedUserIds(allIds);
            calculateStatistics(users, allIds);
        } else {
            setSelectedUserIds([]);
            calculateStatistics(users, []);
        }
    };

    // 일괄등록 실행
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            if (!dateRange || dateRange.length !== 2) {
                message.error('연차 기간을 선택해주세요');
                return;
            }

            if (selectedUserIds.length === 0) {
                message.error('연차를 등록할 사용자를 선택해주세요');
                return;
            }

            setSubmitLoading(true);

            const requestData = {
                start_date: dateRange[0].format('YYYY-MM-DD'),
                end_date: dateRange[1].format('YYYY-MM-DD'),
                user_ids: selectedUserIds,
                leave_type: leaveType,
                reason: '관리자 일괄등록'
            };

            const response = await attendanceAPI.createBulkAnnualLeave(requestData);

            if (response.data.success) {
                const { results } = response.data;
                
                message.success(
                    `연차 일괄등록이 완료되었습니다. (성공: ${results.success_count}명, 실패: ${results.failed_count}명)`
                );

                // 실패한 사용자가 있으면 알림
                if (results.failed_count > 0) {
                    const failedUsers = results.failed_users;
                    console.log('Failed users:', failedUsers);
                    
                    Modal.warning({
                        title: '일부 사용자 등록 실패',
                        content: (
                            <div>
                                <p>다음 사용자들의 연차 등록에 실패했습니다:</p>
                                <ul>
                                    {failedUsers.slice(0, 5).map((user, index) => (
                                        <li key={index}>
                                            {user.user_name}: {user.reason}
                                        </li>
                                    ))}
                                </ul>
                                {failedUsers.length > 5 && (
                                    <p>... 외 {failedUsers.length - 5}명</p>
                                )}
                            </div>
                        ),
                        width: 600
                    });
                }

                onSuccess();
                onCancel();
            }
        } catch (error) {
            message.error(error.response?.data?.message || '연차 일괄등록에 실패했습니다');
            console.error('Bulk leave error:', error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const columns = [
        {
            title: (
                <Checkbox 
                    indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < users.length}
                    checked={users.length > 0 && selectedUserIds.length === users.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                >
                    전체 선택
                </Checkbox>
            ),
            dataIndex: 'selected',
            width: 100,
            render: (_, record) => (
                <Checkbox
                    checked={selectedUserIds.includes(record.id)}
                    onChange={(e) => handleUserSelection(record.id, e.target.checked)}
                />
            )
        },
        {
            title: '사번',
            dataIndex: 'employee_id',
            width: 100
        },
        {
            title: '이름',
            dataIndex: 'name',
            width: 100
        },
        {
            title: '소속부서',
            dataIndex: ['department', 'name'],
            width: 120,
            render: (deptName, record) => (
                <Tag color="blue">{record.department?.name || '미지정'}</Tag>
            )
        },
        {
            title: '총 연차',
            dataIndex: ['balance', 'total_annual_leave'],
            width: 80,
            align: 'center',
            render: (days) => `${Number(days).toFixed(1)}일`
        },
        {
            title: '사용연차',
            dataIndex: ['balance', 'used_annual_leave'],
            width: 80,
            align: 'center',
            render: (days) => `${Number(days).toFixed(1)}일`
        },
        {
            title: '잔여연차',
            dataIndex: ['balance', 'remaining_annual_leave'],
            width: 80,
            align: 'center',
            render: (days) => (
                <span style={{ 
                    color: days < 3 ? '#cf1322' : '#52c41a', 
                    fontWeight: 'bold' 
                }}>
                    {Number(days).toFixed(1)}일
                </span>
            )
        },
        {
            title: '사용률',
            key: 'usage_rate',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const rate = (record.balance.used_annual_leave / record.balance.total_annual_leave) * 100;
                return (
                    <Progress
                        percent={rate}
                        size="small"
                        format={(percent) => `${percent.toFixed(0)}%`}
                        strokeColor={rate > 80 ? '#52c41a' : rate > 50 ? '#faad14' : '#ff7875'}
                    />
                );
            }
        }
    ];

    const calculateLeaveDays = () => {
        if (!dateRange || dateRange.length !== 2) return 0;
        
        if (leaveType === 'morning_half' || leaveType === 'afternoon_half') {
            return 0.5;
        }
        
        const start = dateRange[0];
        const end = dateRange[1];
        const days = end.diff(start, 'day') + 1;
        
        return days;
    };

    const handleLeaveTypeChange = (value) => {
        setLeaveType(value);
        
        // 반차 선택 시 날짜 범위를 하루로 제한
        if ((value === 'morning_half' || value === 'afternoon_half') && dateRange && dateRange.length === 2) {
            const singleDate = dateRange[0];
            setDateRange([singleDate, singleDate]);
            form.setFieldsValue({
                date_range: [singleDate, singleDate]
            });
        }
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CalendarOutlined />
                    <span>연차 일괄등록</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={1200}
            style={{ top: 20 }}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    취소
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    loading={submitLoading}
                    onClick={handleSubmit}
                    disabled={selectedUserIds.length === 0 || !dateRange}
                >
                    확정 ({selectedUserIds.length}명)
                </Button>
            ]}
        >
            <div style={{ marginBottom: 16 }}>
                <Alert
                    message="연차 일괄등록 안내"
                    description="선택한 기간과 사용자들에게 연차를 일괄로 등록합니다. 등록된 연차는 관리자 승인 상태로 즉시 확정됩니다."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <Card size="small" style={{ marginBottom: 16 }}>
                    <Form form={form} layout="vertical">
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="연차 구분"
                                    name="leave_type"
                                    initialValue="full_day"
                                    rules={[
                                        { required: true, message: '연차 구분을 선택해주세요' }
                                    ]}
                                >
                                    <Radio.Group 
                                        value={leaveType} 
                                        onChange={(e) => handleLeaveTypeChange(e.target.value)}
                                        buttonStyle="solid"
                                    >
                                        <Radio.Button value="full_day">종일</Radio.Button>
                                        <Radio.Button value="morning_half">오전 반차</Radio.Button>
                                        <Radio.Button value="afternoon_half">오후 반차</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col span={16}>
                                <Form.Item
                                    label={leaveType === 'full_day' ? "연차 기간" : "반차 날짜"}
                                    name="date_range"
                                    rules={[
                                        { required: true, message: `${leaveType === 'full_day' ? '연차 기간을' : '반차 날짜를'} 선택해주세요` }
                                    ]}
                                >
                                    {leaveType === 'full_day' ? (
                                        <RangePicker
                                            style={{ width: '100%' }}
                                            placeholder={['시작일', '종료일']}
                                            onChange={handleDateRangeChange}
                                            allowClear
                                            format="YYYY-MM-DD"
                                        />
                                    ) : (
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            placeholder="날짜 선택"
                                            onChange={(date) => {
                                                const range = date ? [date, date] : null;
                                                handleDateRangeChange(range);
                                            }}
                                            allowClear
                                            format="YYYY-MM-DD"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        {dateRange && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">
                                    {leaveType === 'full_day' ? (
                                        <>선택한 기간: {dateRange[0].format('YYYY년 MM월 DD일')} ~ {dateRange[1].format('YYYY년 MM월 DD일')}</>
                                    ) : (
                                        <>선택한 날짜: {dateRange[0].format('YYYY년 MM월 DD일')} ({leaveType === 'morning_half' ? '오전 반차' : '오후 반차'})</>
                                    )}
                                    <span style={{ marginLeft: 8, fontWeight: 'bold', color: '#1890ff' }}>
                                        ({calculateLeaveDays()}일)
                                    </span>
                                </Text>
                            </div>
                        )}
                    </Form>
                </Card>

                {statistics && (
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="전체 직원"
                                    value={statistics.totalUsers}
                                    prefix={<TeamOutlined />}
                                    suffix="명"
                                    valueStyle={{ fontSize: 18 }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="선택된 직원"
                                    value={statistics.totalSelected}
                                    prefix={<UserOutlined />}
                                    suffix="명"
                                    valueStyle={{ fontSize: 18, color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="평균 잔여연차"
                                    value={statistics.averageRemaining}
                                    precision={1}
                                    prefix={<CheckCircleOutlined />}
                                    suffix="일"
                                    valueStyle={{ fontSize: 18, color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="연차부족 직원"
                                    value={statistics.lowLeaveUsers}
                                    prefix={<WarningOutlined />}
                                    suffix="명"
                                    valueStyle={{ 
                                        fontSize: 18, 
                                        color: statistics.lowLeaveUsers > 0 ? '#cf1322' : '#52c41a' 
                                    }}
                                />
                            </Card>
                        </Col>
                    </Row>
                )}

                {statistics && statistics.lowLeaveUsers > 0 && (
                    <Alert
                        message={`잔여 연차가 3일 미만인 직원이 ${statistics.lowLeaveUsers}명 있습니다. 연차 등록 전 확인이 필요합니다.`}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Divider orientation="left">
                    <Space>
                        <UserOutlined />
                        사용자 목록
                        <Text type="secondary">({selectedUserIds.length}/{users.length}명 선택)</Text>
                    </Space>
                </Divider>
            </div>

            <Table
                loading={loading}
                dataSource={users}
                columns={columns}
                rowKey="id"
                size="small"
                scroll={{ x: 800, y: 400 }}
                pagination={{
                    size: 'small',
                    showSizeChanger: false,
                    showQuickJumper: true,
                    showTotal: (total, range) => 
                        `${range[0]}-${range[1]} / 총 ${total}명`
                }}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectedUserIds,
                    onChange: (selectedKeys) => {
                        setSelectedUserIds(selectedKeys);
                        calculateStatistics(users, selectedKeys);
                    },
                    getCheckboxProps: (record) => ({
                        disabled: false
                    })
                }}
            />
        </Modal>
    );
};

export default BulkAnnualLeaveModal;